import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { toast } from 'sonner';
import { getRoleClient } from '@/lib/api-clients';
import { CACHE_TIME_10_MINS } from '@/lib/query-client';
import { queryKeys } from '@/lib/query-keys';
import type {
  CreateRoleRequest,
  UpdateRoleRequest,
  RoleSummary,
  RoleResponse,
} from '@vagusx/clients';

export function useRoles(options: { enabled?: boolean } = {}): UseQueryResult<RoleResponse[]> {
  return useQuery({
    queryKey: queryKeys.roles.list(),
    queryFn: () => getRoleClient().list(),
    // Roles change only via admin action. Same-session mutations
    // (`useCreateRole` / `useUpdateRole` / `useToggleRoleStatus`)
    // invalidate this cache, so the user who made the change sees it
    // immediately. For changes made elsewhere, the shared 10-minute
    // cache window applies.
    staleTime: CACHE_TIME_10_MINS,
    enabled: options.enabled ?? true,
  });
}

export function useRole(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.roles.detail(id!),
    queryFn: () => getRoleClient().getById(id!),
    enabled: !!id,
  });
}

export function useAvailablePermissions() {
  return useQuery({
    queryKey: queryKeys.roles.permissions(),
    queryFn: () => getRoleClient().getPermissions(),
  });
}

export function useCreateRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateRoleRequest) => getRoleClient().create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.roles.all });
      toast.success('Role created');
    },
  });
}

export function useUpdateRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateRoleRequest }) =>
      getRoleClient().update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.roles.all });
      toast.success('Role updated');
    },
  });
}

export function useToggleRoleStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => getRoleClient().toggleStatus(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.roles.all });
      toast.success(data.rowStatus === 0 ? 'Role activated' : 'Role deactivated');
    },
  });
}

/**
 * Returns a Map<roleId, RoleSummary> for client-side role resolution.
 * Org roles are fetched once and cached by TanStack Query. Pass
 * `enabled: false` (e.g. while a picker popover is closed) to skip the
 * fetch entirely — the map will be empty until enabled flips true, then
 * populates on the next render.
 */
export function useRoleMap(options: { enabled?: boolean } = {}): Map<string, RoleSummary> {
  const { data: orgRoles } = useRoles(options);
  return useMemo(() => {
    const map = new Map<string, RoleSummary>();
    orgRoles?.forEach((r) =>
      map.set(r.id, { id: r.id, name: r.name, slug: r.slug, isSystem: r.isSystem })
    );
    return map;
  }, [orgRoles]);
}

/**
 * Resolve an array of roleIds to RoleSummary objects using a role map.
 */
export function resolveRoles(roleIds: string[], roleMap: Map<string, RoleSummary>): RoleSummary[] {
  return roleIds.map((id) => roleMap.get(id)).filter((r): r is RoleSummary => r !== undefined);
}
